import React, { useState, useRef, useEffect } from 'react';
import { Database, Calendar, Bell, FileCheck, BarChart3, Columns, Info, X, TrendingUp, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Tooltip } from '@mui/material';
import heartLoading from '../../../assests/heartLoading.gif';
import AssetConfiguration from '../Domain/AssetConfiguration';
import { DataContractDetailsProduct } from '../DataTrafficLights/DataContractDetailsProduct';
import {
  ProductOverviewTab,
  NotificationsTab,
  AssetOverviewTab,
  AssetSchemaTab,
  AssetProfilingTab,
  AssetDQRulesTab
} from './ProductViewComponents';

export default function ProductView({ product, onClose }) {
  const [selectedView, setSelectedView] = useState('product-overview');
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarWidth, setSidebarWidth] = useState(256);
  const [isResizing, setIsResizing] = useState(false);
  const [selectedAssetData, setSelectedAssetData] = useState(null);
  const [assetLoading, setAssetLoading] = useState(false);
  const sidebarRef = useRef(null);

  // Traffic light state
  const [trafficContract, setTrafficContract] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [trafficError, setTrafficError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [consumerData, setConsumerData] = useState(null);

  // Handle sidebar resizing
  const startResizing = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      if (sidebarRef.current) {
        const sidebarRect = sidebarRef.current.getBoundingClientRect();
        const newWidth = e.clientX - sidebarRect.left;
        if (newWidth >= 200 && newWidth <= 400) {
          setSidebarWidth(newWidth);
        }
      }
    };

    const handleMouseUp = () => setIsResizing(false);

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Fetch traffic contract when traffic tab becomes active
  useEffect(() => {
    if (
      selectedView === 'traffic' &&
      product?.ProductInfo?.productId &&
      !trafficContract
    ) {
      fetchTrafficContract(product.ProductInfo.productId);
    }
  }, [selectedView]);

  // Fetch asset details when an asset is selected
  const fetchAssetDetails = async (assetId) => {
    if (!assetId) return;

    setAssetLoading(true);
    setSelectedAssetData(null);

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/asset-overview-v2/${assetId}`);
      setSelectedAssetData(response.data);
    } catch (error) {
      console.error('Error fetching asset details:', error);
      toast.error('Failed to load asset details');
      setSelectedAssetData(null);
    } finally {
      setAssetLoading(false);
    }
  };

  const fetchTrafficContract = async (productId) => {
    try {
      setTrafficLoading(true);
      setTrafficError(null);

      // Step 1: fetch contract first to get contract.id
      const contractResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-data-contract/${productId}`
      );

      if (!contractResponse.data) {
        setTrafficError('No contract data found.');
        return;
      }

      // Step 2: fetch consumer dependencies using contract.id
      const consumerResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-consumer-dependencies/${contractResponse.data.id}`
      );

      // Both done — set both at once — ONE loader total
      setTrafficContract(contractResponse.data);
      setConsumerData(consumerResponse.data);

    } catch (err) {
      console.error('Failed to fetch traffic contract:', err);
      setTrafficError('Failed to load traffic light data. Please try again.');
    } finally {
      setTrafficLoading(false);
    }
  };

  const handleCopyLink = async (event) => {
    if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
    try {
      const linkToCopy = `${window.location.origin}/data-traffic-lights?viewProduct=${trafficContract?.name}`;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(linkToCopy);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = linkToCopy;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedId(trafficContract?.name);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy link', error);
    }
  };

  // Check if data is loaded
  const isLoading = !product || !product.ProductInfo;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img src={heartLoading} alt="Loading..." className="w-24 h-24 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading product details...</p>
        </div>
      </div>
    );
  }

  // Extract data from product prop
  const productInfo = product?.ProductInfo || {};
  const slaAndAlerts = product?.SLAandAlerts || {};
  const assetList = product?.AssetList || [];

  // Map AssetList to simplified array for sidebar display
  const assetsArray = assetList.map((asset) => ({
    id: asset.id,
    name: asset.AssetName || 'N/A'
  }));

  const productOverview = {
    productName: productInfo.productName || 'N/A',
    productCode: productInfo.productCode || 'N/A',
    description: productInfo.description || 'N/A',
    productOwner: productInfo.productOwner || 'N/A',
    parentDomainName: productInfo.parentDomainName || 'N/A',
    domainName: productInfo.domainName || 'N/A',
    subDomainName: productInfo.subDomainName || 'N/A',
    dataLayer: productInfo.dataLayer || 'N/A',
    productTag: productInfo.productTag || [],
    productDocumentationUrl: productInfo.productDocumentationUrl || 'N/A',
    status: productInfo.status || 'N/A',
    parentRegion: productInfo?.parentRegion || 'N/A',
    marketRegion: productInfo.marketRegion || [],
    marketCountry: productInfo.marketCountry || [],
    lineOfBusiness: productInfo.lineOfBusiness || 'N/A',
    minExpectedUsers: productInfo.minExpectedUsers || 'N/A',
    maxExpectedUsers: productInfo.maxExpectedUsers || 'N/A',
    assetSource: productInfo.assetSource || 'N/A',
    sizeThreshold: productInfo.sizeThreshold || null,
    valueMapping: productInfo.valueMapping || 'N/A',
    reportDetails: productInfo.reportDetails || []
  };

  // Get the current selected asset from sidebar
  const currentAssetFromList = typeof selectedView === 'number' ? assetsArray[selectedView] : null;

  // Build currentAsset from fetched data for use in tab components
  const currentAsset = selectedAssetData ? {
    key: currentAssetFromList?.id,
    name: selectedAssetData.Configuration?.assetName || currentAssetFromList?.name,
    dataAssetOwner: selectedAssetData.Configuration?.dataAssetOwner || 'N/A',
    Configuration: selectedAssetData.Configuration || {},
    tableDetails: selectedAssetData.tableDetails || [],
    ProfilingInfo: selectedAssetData.ProfilingInfo || [],
    DQInfo: selectedAssetData['DQ Info'] || {}
  } : null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Database },
    { id: 'schema', label: 'Schema', icon: Columns },
    { id: 'schedule', label: 'Schedule', icon: Calendar },
    { id: 'profiling', label: 'Profiling', icon: BarChart3 },
    { id: 'dq-rules', label: 'DQ Rules', icon: FileCheck }
  ];

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden relative">
      {/* Close Button */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-colors shadow-sm"
          title="Close"
        >
          <X className="h-5 text-gray-600" />
        </button>
      )}

      {/* Left Sidebar Navigation */}
      <div
        ref={sidebarRef}
        className="bg-white border-r border-gray-200 flex flex-col h-full flex-shrink-0"
        style={{ width: `${sidebarWidth}px` }}
      >
        {/* General Sections */}
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">General</h3>
          <button
            onClick={() => setSelectedView('product-overview')}
            className={`w-full text-left p-3 rounded-lg mb-2 transition-colors flex items-center gap-2 ${selectedView === 'product-overview'
                ? 'bg-blue-50 border border-blue-200 text-blue-900'
                : 'hover:bg-gray-50 border border-transparent text-gray-700'
              }`}
          >
            <Info className="h-4" />
            <span className="text-sm font-medium">Product Overview</span>
          </button>
          <button
            onClick={() => setSelectedView('notifications')}
            className={`w-full text-left p-3 rounded-lg mb-2 transition-colors flex items-center gap-2 ${selectedView === 'notifications'
                ? 'bg-blue-50 border border-blue-200 text-blue-900'
                : 'hover:bg-gray-50 border border-transparent text-gray-700'
              }`}
          >
            <Bell className="h-4" />
            <span className="text-sm font-medium">Notifications</span>
          </button>
          <button
            onClick={() => setSelectedView('traffic')}
            className={`w-full text-left p-3 rounded-lg mb-2 transition-colors flex items-center gap-2 ${selectedView === 'traffic'
                ? 'bg-blue-50 border border-blue-200 text-blue-900'
                : 'hover:bg-gray-50 border border-transparent text-gray-700'
              }`}
          >
            <TrendingUp className="h-4" />
            <span className="text-sm font-medium">Traffic Light</span>
          </button>
          <button
            onClick={() => setSelectedView('cost')}
            className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-2 ${selectedView === 'cost'
                ? 'bg-blue-50 border border-blue-200 text-blue-900'
                : 'hover:bg-gray-50 border border-transparent text-gray-700'
              }`}
          >
            <DollarSign className="h-4" />
            <span className="text-sm font-medium">Cost</span>
          </button>
        </div>

        {/* Tables Section */}
        <div className="flex-1 overflow-y-auto p-4 border-b border-gray-200">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Tables ({assetsArray.length})</h3>
          {assetsArray.map((asset, index) => (
            <button
              key={asset.id}
              onClick={() => {
                setSelectedView(index);
                setActiveTab('overview');
                fetchAssetDetails(asset.id);
              }}
              className={`w-full text-left p-3 rounded-lg mb-2 transition-colors overflow-hidden ${selectedView === index
                  ? 'bg-blue-50 border border-blue-200'
                  : 'hover:bg-gray-50 border border-transparent'
                }`}
            >
              <div className="overflow-hidden">
                <span
                  className={`text-sm font-medium block truncate ${selectedView === index ? 'text-blue-900' : 'text-gray-900'
                    }`}
                  title={asset.name}
                >
                  {asset.name}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Resize Handle */}
      <div
        onMouseDown={startResizing}
        className="flex-shrink-0 cursor-col-resize group h-full"
        style={{ width: '8px' }}
      >
        <div className={`w-1 h-full bg-gray-300 group-hover:bg-blue-500 transition-colors duration-200 mx-auto ${isResizing ? 'bg-blue-500' : ''}`}></div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Review Product</h1>
              <p className="text-gray-600 mt-1">You can check all the details of the product</p>
            </div>
            <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
              Read Only
            </div>
          </div>
        </div>

        {/* Product Overview Content */}
        {selectedView === 'product-overview' && (
          <ProductOverviewTab productOverview={productOverview} />
        )}

        {/* Notifications Content */}
        {selectedView === 'notifications' && (
          <NotificationsTab slaAndAlerts={slaAndAlerts} />
        )}

        {/* Traffic Light Content */}
        {selectedView === 'traffic' && (
          <div className="flex-1 overflow-y-auto">
            {trafficLoading && (
              <div className="flex items-center justify-center min-h-[60vh]">
                <div className="flex flex-col items-center space-y-3">
                  <img src={heartLoading} alt="Loading..." className="h-30" />
                  <span className="text-gray-700 text-[20px] text-center">
                    Loading traffic light data...
                  </span>
                </div>
              </div>
            )}

            {!trafficLoading && trafficError && (
              <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                <TrendingUp className="w-16 h-16 text-gray-300" />
                <p className="text-gray-500">{trafficError}</p>
                <button
                  onClick={() => fetchTrafficContract(product.ProductInfo.productId)}
                  className="px-4 py-2 bg-[#bb1ccc] text-white rounded-lg text-sm hover:bg-blue-800 transition-colors"
                >
                  Retry
                </button>
              </div>
            )}

            {!trafficLoading && !trafficError && trafficContract && (
              <div className="px-6">
                <div className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <h3 className="text-xl font-bold text-gray-900">{trafficContract.name}</h3>
                  <Tooltip
                    title={copiedId === trafficContract.name ? 'Copied!' : 'Copy direct link'}
                    arrow
                    placement="right"
                    PopperProps={{ sx: { zIndex: 100000000 } }}
                  >
                    <span className="inline-flex">
                      <button
                        onClick={handleCopyLink}
                        style={{
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                          display: 'inline-flex',
                          backgroundColor: copiedId === trafficContract.name ? '#15803d' : '#1D7285',
                          color: 'white',
                          padding: '5px 8px',
                          borderRadius: '3px',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                        }}
                      >
                        {copiedId === trafficContract.name ? '✓ Copied' : 'Copy Link'}
                      </button>
                    </span>
                  </Tooltip>
                </div>

                {/* Pass prefetchedConsumerData so no second loader */}
                <DataContractDetailsProduct
                  contract={trafficContract}
                  prefetchedConsumerData={consumerData}
                />
              </div>
            )}

            {!trafficLoading && !trafficError && !trafficContract && (
              <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <TrendingUp className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-gray-500">No traffic light data available for this product.</p>
              </div>
            )}
          </div>
        )}

        {/* Cost Content */}
        {selectedView === 'cost' && (
          <div className="p-6 h-[60vh] flex flex-col items-center justify-center text-center">
            <DollarSign className="w-16 text-gray-400 mb-4" />
            <p className="text-gray-600">Cost analysis content coming soon...</p>
          </div>
        )}

        {/* Table-specific content */}
        {typeof selectedView === 'number' && (
          <>
            {assetLoading && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <img src={heartLoading} alt="Loading..." className="w-20 h-20 mx-auto mb-4" />
                  <p className="text-gray-600">Loading asset details...</p>
                </div>
              </div>
            )}

            {!assetLoading && currentAsset && (
              <>
                <div className="bg-white border-b border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">{currentAsset.Configuration?.assetName || currentAsset.name}</h2>
                      <p className="text-sm text-gray-600 mt-1">{currentAsset.Configuration?.assetDescription || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border-b border-gray-200 px-6">
                  <div className="flex space-x-1 overflow-x-auto">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${activeTab === tab.id
                              ? 'text-blue-600 border-b-2 border-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                          <Icon className="w-4 h-4" />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  {activeTab === 'overview' && <AssetOverviewTab currentAsset={currentAsset} />}
                  {activeTab === 'schema' && <AssetSchemaTab currentAsset={currentAsset} />}
                  {activeTab === 'schedule' && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <AssetConfiguration assetOverview={currentAsset.Configuration} />
                    </div>
                  )}
                  {activeTab === 'profiling' && <AssetProfilingTab currentAsset={currentAsset} />}
                  {activeTab === 'dq-rules' && <AssetDQRulesTab currentAsset={currentAsset} />}
                </div>
              </>
            )}

            {!assetLoading && !currentAsset && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <p>Failed to load asset details. Please try again.</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}